import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import {
  Avatar,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";

import Breadcrumbs from "../../common2/Breadcrumbs";
import TopFilterNew from "../../common2/TopFilterNew";
import AddNewPart from "../../common/AddNew";

import editIcon from "../../../assest/icons/edit.svg";
import deleteIcon from "../../../assest/icons/delete.svg";
import restoreIcon from "../../../assest/icons/restore.svg";
import { EyeIcon, EnableUserIcon, DisableUserIcon } from "../../common/svgIcons/chartIcon";

import { useDispatch, useSelector } from "react-redux";
import { IState } from "../../../redux/reducers/rootReducers";
import { useNavigate } from "react-router-dom";

import {
  getAzureUsers,
  resetAzureUsers,
  disableAzureUser,
  enableAzureUser,
  softDeleteAzureUser,
  restoreAzureUser,
  hardDeleteAzureUser,
} from "../../../redux/actions/userManagement/azureUsers";

import "../../ContactsManagement/contact.css";
import "./azureUsers.css";

import RefreshIcon from "@mui/icons-material/Refresh";

type StatusFilter = "all" | "active" | "inactive" | "deleted";
const pageSize         = 50;
const MAX_POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS  = 3000;

// ─────────────────────────────────────────────────────────────
function AzureUsers() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const [searchData, setSearchData] = useState("");
  const [status, setStatus]         = useState<StatusFilter>("all");

  // True while we are doing the 5s wait + api re-fetch after any action
  const [syncing, setSyncing] = useState(false);

  // Keep latest filter values accessible inside async functions without stale closures
  const statusRef = useRef(status);
  const searchRef = useRef(searchData);
  useEffect(() => { statusRef.current = status; },     [status]);
  useEffect(() => { searchRef.current = searchData; }, [searchData]);

  const azureUsersState   = useSelector((state: IState) => state?.azureUserReducer?.Data);
  const users             = azureUsersState?.data    || [];
  const loading           = azureUsersState?.loading || false;
  const hasMore           = azureUsersState?.meta?.hasMore           || false;
  const continuationToken = azureUsersState?.meta?.continuationToken || null;
  const error             = azureUsersState?.error   || "";

  // ── Initial / filter-driven fetch ──────────────────────────
  useEffect(() => {
    if (syncing) return;
    const t = setTimeout(() => {
      dispatch(resetAzureUsers());
      dispatch(getAzureUsers({ status, pageSize, continuationToken: null, search: searchData?.trim() || null }));
    }, 350);
    return () => clearTimeout(t);
  }, [dispatch, status, searchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => {
    dispatch(resetAzureUsers());
    dispatch(getAzureUsers({ status, pageSize, continuationToken: null, search: searchData?.trim() || null }));
  };

  const loadMore = () => {
    if (!hasMore || !continuationToken || loading) return;
    dispatch(getAzureUsers({ status, pageSize, continuationToken, search: searchData?.trim() || null, append: true }));
  };

  // ── Core: run action → poll until the change is visible ────
  // isDone receives the freshly-fetched user list and returns true
  // once the expected change is actually reflected in the data.
  const runAction = useCallback(async (
    actionFn: () => Promise<any>,
    isDone?: (users: any[]) => boolean,
  ) => {
    try {
      await actionFn();
    } catch (err) {
      console.error("Azure action failed", err);
      return;
    }

    setSyncing(true);

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      const changed = await new Promise<boolean>((resolve) => {
        dispatch(
          getAzureUsers(
            {
              status: statusRef.current,
              pageSize,
              continuationToken: null,
              search: searchRef.current?.trim() || null,
            },
            (cbStatus: "success" | "failure", data: any) => {
              if (cbStatus !== "success" || !isDone) { resolve(true); return; }
              const list: any[] = Array.isArray(data) ? data : [];
              resolve(isDone(list));
            }
          )
        );
      });

      if (changed) break;
    }

    setSyncing(false);
  }, [dispatch]);

  const filterCallback = useCallback((values: any) => {
    setSearchData(values?.search || "");
    setStatus((values?.status || "all") as StatusFilter);
  }, []);

  const openCreate = () => navigate("/AzureManagement/AzureUsers/add");
  const openEdit   = (u: any) => navigate(`/AzureManagement/AzureUsers/edit/${u.id}`);

  const onEnableDisable = (u: any) =>
    runAction(
      () => u.status === "Active" ? dispatch(disableAzureUser(u.id)) : dispatch(enableAzureUser(u.id)),
      (list) => { const found = list.find((x: any) => x.id === u.id); return !found || found.status !== u.status; }
    );

  const onSoftDelete = (u: any) =>
    runAction(
      () => dispatch(softDeleteAzureUser(u.id)),
      (list) => { const found = list.find((x: any) => x.id === u.id); return !found || found.status === "Deleted"; }
    );

  const onRestore = (u: any) =>
    runAction(
      () => dispatch(restoreAzureUser(u.id)),
      (list) => { const found = list.find((x: any) => x.id === u.id); return !found || found.status === "Active"; }
    );

  const onHardDelete = (u: any) =>
    runAction(
      () => dispatch(hardDeleteAzureUser(u.id)),
      (list) => !list.find((x: any) => x.id === u.id)
    );

  // Show loader when syncing (5s wait + re-fetch) OR on initial load
  const showLoader = syncing || (loading && users.length === 0);

  return (
    <>
      <Breadcrumbs
        data={[
          { title: "Contact Management ", path: "/ContactManagement" },
          { title: "Azure Users ",        path: "/AzureManagement/AzureUsers" },
        ]}
      />

      <Box style={{ position: "sticky", zIndex: "999", top: "111px" }}>
        <TopFilterNew
          callback={filterCallback}
          addHandler={openCreate}
          backNavigationPath="/ContactManagement"
          useStatusButtons={true}
          defaultStatus="all"
          pushExtraFeaturesRight={true}
          statusOptions={[
            { label: "Show All",       value: "all"      },
            { label: "Show Active",    value: "active"   },
            { label: "Show In-Active", value: "inactive" },
            { label: "Show Deleted",   value: "deleted"  },
          ]}
          extraFeatures={
            <Button
              size="small"
              className="common-btn"
              onClick={refresh}
              disabled={loading || syncing}
              sx={{ minWidth: "unset", px: 1.2 }}
            >
              <RefreshIcon fontSize="small" />
            </Button>
          }
        />
      </Box>

      <Box className="pageHolder">
        {error && !syncing ? (
          <Box sx={{ px: 1, pb: 1 }}>
            <Typography variant="body2" color="error">{String(error)}</Typography>
          </Box>
        ) : null}

        {showLoader ? (
          /* ── Full loader: shown for initial load AND during any action ── */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 300,
              gap: 2,
            }}
          >
            <CircularProgress size={48} thickness={4} />
            <Typography variant="body1" fontWeight={600} color="text.primary">
              {syncing ? "Syncing with Azure AD…" : "Loading…"}
            </Typography>
          </Box>
        ) : (
          <InfiniteScroll
            dataLength={users.length}
            next={loadMore}
            hasMore={hasMore}
            loader={
              <Box className="p-2" sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Box>
            }
          >
            <Grid container className="pt-1" spacing={1}>
              {users.length > 0 ? (
                users.map((u: any, index: number) => (
                  <AzureUserCard
                    key={u.id || index}
                    item={u}
                    navigate={navigate}
                    onEdit={openEdit}
                    onEnableDisable={onEnableDisable}
                    onSoftDelete={onSoftDelete}
                    onRestore={onRestore}
                    onHardDelete={onHardDelete}
                  />
                ))
              ) : (
                <Grid item xs={12}>
                  <AddNewPart title="Add Azure User" path="/AzureManagement/AzureUsers/add" />
                </Grid>
              )}
            </Grid>
          </InfiniteScroll>
        )}
      </Box>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
type AzureUserCardProps = {
  item: any;
  navigate: any;
  onEdit: (u: any) => void;
  onEnableDisable: (u: any) => void;
  onSoftDelete: (u: any) => void;
  onRestore: (u: any) => void;
  onHardDelete: (u: any) => void;
};

const AzureUserCard = memo((props: AzureUserCardProps) => {
  const { item, navigate, onEdit, onEnableDisable, onSoftDelete, onRestore, onHardDelete } = props;
  const { id, name, userName, email, mobilePhone, officeLocation, status } = item;

  const displayTitle  = userName || name        || "------";
  const displayName   = name                    || "------";
  const displayEmail  = email                   || "------";
  const displayOffice = officeLocation          || "------";
  const displayMobile = mobilePhone             || "------";
  const initial = (displayTitle || "A").charAt(0).toUpperCase();

  return (
    <Grid item md={3} xs={12} className="cardlist">
      <Box className="contact-card azure-contact-card">
        <Box className="contact-card-header">
          <Box className="icon-holder">
            <Avatar className="sml-avatar azure-user-avatar" sx={{ height: 25, width: 25 }}>
              {initial}
            </Avatar>
            <Tooltip title={displayTitle}>
              <Typography variant="body2" className="azure-user-title">
                &nbsp;{displayTitle}
              </Typography>
            </Tooltip>
          </Box>
          <Box className={`azure-user-status ${String(status || "").toLowerCase()}`}>
            {status || "Unknown"}
          </Box>
        </Box>

        <Box className="contact-card-body txt-ellipsis">
          <Typography variant="body1">Name : {displayName}</Typography>
          <Typography variant="body1">Organisation : Azure AD</Typography>
          <Typography variant="body1">Type : Azure User</Typography>
          <Typography variant="body1">Location : {displayOffice}</Typography>
          <Typography variant="body1">Mobile : {displayMobile}</Typography>
          <Typography variant="body1">Email : {displayEmail}</Typography>
        </Box>

        <Box className="contact-card-footer azure-card-footer">
          {(status === "Active" || status === "Inactive") && (
            <Tooltip title={status === "Active" ? "Disable" : "Enable"}>
              <button
                type="button"
                className={`azure-action-btn ${status === "Active" ? "lock-active" : "lock-inactive"}`}
                onClick={() => onEnableDisable(item)}
              >
                {status === "Active" ? <DisableUserIcon /> : <EnableUserIcon />}
              </button>
            </Tooltip>
          )}

          {status !== "Deleted" && (
            <Tooltip title="View">
              <button
                type="button"
                className="azure-action-btn view-btn"
                onClick={() => navigate(`/AzureManagement/AzureUsers/${id}`)}
              >
                <EyeIcon />
              </button>
            </Tooltip>
          )}

          {status !== "Deleted" && (
            <Tooltip title="Edit">
              <button
                type="button"
                className="azure-action-btn edit-btn"
                onClick={() => onEdit(item)}
              >
                <img src={editIcon} alt="edit" />
              </button>
            </Tooltip>
          )}

          {status !== "Deleted" ? (
            <Tooltip title="Delete (Soft)">
              <button
                type="button"
                className="azure-action-btn delete-btn"
                onClick={() => onSoftDelete(item)}
              >
                <img src={deleteIcon} alt="delete" />
              </button>
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Restore">
                <button
                  type="button"
                  className="azure-action-btn restore-btn"
                  onClick={() => onRestore(item)}
                >
                  <img src={restoreIcon} alt="restore" />
                </button>
              </Tooltip>
              <Tooltip title="Hard Delete">
                <button
                  type="button"
                  className="azure-action-btn delete-btn"
                  onClick={() => onHardDelete(item)}
                >
                  <img src={deleteIcon} alt="hard delete" />
                </button>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    </Grid>
  );
});

export default AzureUsers;